import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllLanguages,
  getLanguageById,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  getLanguageDirections,
  getLanguageCategories,
  getTranslationSettings
} from "../Actions/LanguageAction";
import { 
  Language, 
  LanguageResponse, 
  LanguageDirection, 
  LanguageCategory, 
  TranslationSettings 
} from "../../modules/shared/model/LanguageInterfaces";

interface LanguageState {
  languages: Language[];
  selectedLanguage: Language | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
  currentLimit: number;
  directions: LanguageDirection[];
  categories: LanguageCategory[];
  translationSettings: TranslationSettings[];
}

const initialState: LanguageState = {
  languages: [],
  selectedLanguage: null,
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  currentPage: 1,
  currentLimit: 10,
  directions: [],
  categories: [],
  translationSettings: []
};

const languageSlice = createSlice({
  name: "language",
  initialState,
  reducers: {
    clearSelected: (state) => {
      state.selectedLanguage = null;
    },
    clearErrorState: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.currentLimit = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Get all languages
    builder
      .addCase(getAllLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllLanguages.fulfilled, (state, action: PayloadAction<LanguageResponse>) => {
        state.loading = false;
        state.languages = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.hasNextPage = action.payload.hasNextPage;
        state.currentPage = action.payload.currentPage;
        state.currentLimit = action.payload.currentLimit;
      })
      .addCase(getAllLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get language by ID
    builder
      .addCase(getLanguageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLanguageById.fulfilled, (state, action: PayloadAction<Language>) => {
        state.loading = false;
        state.selectedLanguage = action.payload;
      })
      .addCase(getLanguageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create language
    builder
      .addCase(createLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLanguage.fulfilled, (state, action: PayloadAction<Language>) => {
        state.loading = false;
        state.languages.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update language
    builder
      .addCase(updateLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLanguage.fulfilled, (state, action: PayloadAction<Language>) => {
        state.loading = false;
        const index = state.languages.findIndex(language => language.id === action.payload.id);
        if (index !== -1) {
          state.languages[index] = action.payload;
        }
        if (state.selectedLanguage?.id === action.payload.id) {
          state.selectedLanguage = action.payload;
        }
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete language
    builder
      .addCase(deleteLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLanguage.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.languages = state.languages.filter(language => language.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedLanguage?.id === action.payload) {
          state.selectedLanguage = null;
        }
      })
      .addCase(deleteLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get language directions
    builder
      .addCase(getLanguageDirections.fulfilled, (state, action: PayloadAction<LanguageDirection[]>) => {
        state.directions = action.payload;
      });

    // Get language categories
    builder
      .addCase(getLanguageCategories.fulfilled, (state, action: PayloadAction<LanguageCategory[]>) => {
        state.categories = action.payload;
      });

    // Get translation settings
    builder
      .addCase(getTranslationSettings.fulfilled, (state, action: PayloadAction<TranslationSettings[]>) => {
        state.translationSettings = action.payload;
      });
  }
});

export const { clearSelected, clearErrorState, setPage, setLimit } = languageSlice.actions;
export default languageSlice.reducer;
